import { useEffect, useMemo, useState } from 'react';
import { Text, Box, Input, InputGroup, InputRightElement, Flex } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import { useGetInfiniteListMasterProduct } from '@pages/UploadMappingV2/hooks/query/useGetListMasterProduct';
import InfiniteScrollObserver from '../InfiniteScrollObserver';
import { IterationComponent } from '@components/functionComponent';
import SkeletonComponent from '@components/skeleton';
import { useMassalStore } from '@pages/UploadMappingV2/hooks/state/useAddProductMassal';
import { EmptyStateInfinite } from '@assets/icons';
import { useApp } from '@pages/UploadMappingV2/context/AppContext';
import { debounce } from '@helpers/utils';
import EmptyState from './EmptyState';

export default function SearchProduct({ id, allValue, isPassing, type }) {
    const { updateBarcode, updateBarcodeBundle } = useMassalStore();
    const { expanded } = useApp();
    const [inputValue, setInputValue] = useState(allValue?.barcode ? allValue.name : '');
    const [searchValue, setSearchValue] = useState('');
    const isShowLists = searchValue?.length > 3 && !allValue.barcode && expanded === id;

    const debouncedSetSearch = useMemo(
        () =>
            debounce((val) => {
                setSearchValue(val);
            }, 500),
        []
    );

    const handleSearch = (e) => {
        const val = e.target.value;
        setInputValue(val);
        debouncedSetSearch(val);
    };

    const handleSelect = (product) => () => {
        const selectedObj = { ...product, id: crypto.randomUUID() };
        setInputValue(product.name);
        if (type === 'bundle') {
            updateBarcodeBundle(id, selectedObj.barcode);
        } else {
            updateBarcode(id, selectedObj.barcode);
        }
    };

    const {
        data: infiniteData,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isLoading: isLoadingInfiniteData,
    } = useGetInfiniteListMasterProduct({ search: searchValue });

    const isProduct = infiniteData?.length > 0 && !isLoadingInfiniteData;

    const productLists = () => {
        const renderItem = (value) => (
            <Box
                key={value.id}
                p="12px"
                transition=".3s all ease"
                cursor="pointer"
                borderRadius="8px"
                _hover={{ bg: 'semantic.200' }}
                onClick={handleSelect(value)}
            >
                <Text mb="4px">{value.name}</Text>
                <Text color="neutral.500" fontSize="14px">
                    {value.barcode}
                </Text>
            </Box>
        );

        const renderSkeleton = () => (
            <IterationComponent
                arr={[1, 2, 3]}
                render={() => (
                    <Box bg="semantic.200" mb="8px" p="12px" borderRadius="8px">
                        <SkeletonComponent width="50%" height="20px" mb="4px" />
                        <SkeletonComponent width="25%" height="17px" />
                    </Box>
                )}
            />
        );

        const Section = ({ title, children }) => (
            <Box mb="12px">
                <Text color="neutral.600" textTransform="uppercase" mb="8px" fontSize="14px">
                    {title}
                </Text>
                {children}
            </Box>
        );

        return (
            <Box
                p="22px"
                borderRadius="8px"
                bg="white"
                shadow="sm"
                position="absolute"
                h="fit-content"
                maxH="400px"
                overflowY={isProduct ? 'scroll' : 'visible'}
                w="full"
                top="50px"
                zIndex={'99'}
            >
                {isProduct ? (
                    <Section title="Produk Lainnya">
                        {!isLoadingInfiniteData ? infiniteData?.map(renderItem) : renderSkeleton()}

                        {hasNextPage && (
                            <>
                                <InfiniteScrollObserver
                                    hasNextPage={hasNextPage}
                                    fetchNextPage={fetchNextPage}
                                    disabled={isFetchingNextPage}
                                    rootMargin="900px"
                                />
                                {isFetchingNextPage && renderSkeleton()}
                            </>
                        )}
                    </Section>
                ) : (
                    <EmptyState />
                )}
            </Box>
        );
    };

    useEffect(() => {
        if (isPassing) {
            setInputValue(allValue.name);
            setSearchValue(allValue.name);
        }
    }, [isPassing]);

    return (
        <Box>
            <InputGroup position={'relative'}>
                <Input
                    background={'semantic.100'}
                    placeholder={'Cari nama atau kode produk Paragon...'}
                    value={inputValue}
                    onChange={handleSearch}
                />
                <InputRightElement mx={'12px'} onClick={() => {}}>
                    {!isShowLists ? <ChevronDownIcon fontSize={'24px'} /> : <ChevronUpIcon fontSize={'24px'} />}
                </InputRightElement>
                {isShowLists && productLists()}
            </InputGroup>
        </Box>
    );
}
